import { CASE_PREFIX, LOCALES, type Locale } from "@/i18n/config";
import { caseProfiles } from "./caseProfiles";
import {
  // e-commerce
  sonderBlack,
  verona,
  sonderHeroTablet,
  sonderProductMobile,
  sonderVirtualTrial,
  veronaHeroTablets,
  veronaDesktopScreens,
  // software factory
  logoEmerger,
  emergerMobileApp,
  emergerManagementTablets,
  marriott,
  marriottLaptopMockup,
  marriottDashboardPanels,
  logoCrucijuegos,
  // ia
  logoEnapro,
  logoPrevinca,
  logoSanCristobalColor,
  logoRiskrator,
  logoBoomid,
  logoElGrafico,
  audioplayerElGraficoWidget,
  audioplayerElGraficoVoiceClone,
} from "@/assets";

/**
 * Modelo unico de caso de exito.
 *
 * Unifica los tres formatos que convivian antes (ecommerceCases,
 * softwareFactoryCases, aiCases). Cada caso tiene su propia pagina en
 * /caso/<slug> (/en/case/<slug> en ingles); las categorias (/casos/<rubro>)
 * son indices que listan los casos del rubro, como el listado de novedades.
 *
 * Todos los campos de contenido son opcionales salvo los de identidad: un caso
 * con poca info (solo prosa, o sin impacto todavia) renderiza las secciones que
 * tiene y omite el resto, en vez de mostrar huecos. Asi conviven Emerger
 * -completo- con Marriott -solo prosa- sin ramas especiales.
 */

export type CaseRubro = "ecommerce" | "software-factory" | "ai";

export interface CaseImage {
  src: string;
  alt: string;
  /** Autoria o licencia, si la imagen no es de WAIA (se muestra como pie). */
  credit?: string;
  creditUrl?: string;
  /** Foto que llena la portada (recortada con cover) en vez de flotar centrada
   * como una captura o un mockup. */
  fill?: boolean;
}

export interface CaseStat {
  value: string;
  label: string;
}

/** Dato duro del cliente (no un resultado de WAIA): su origen va en `sources`. */
export interface CaseFact {
  value: string;
  label: string;
}

export interface CaseSource {
  label: string;
  url: string;
}

export interface CaseTestimonial {
  quote: string;
  name: string;
  role: string;
}

/** Campos de contenido traducibles. Separados de CaseStudy para poder definir
 *  el override en ingles (`en?: Partial<CaseStudyContent>`) sin circularidad. */
interface CaseStudyContent {
  /** Title de la pestaña/buscador (sin la marca). Corto: el title largo de la
   *  tarjeta se cortaba a ~60 caracteres en el resultado de busqueda. */
  seoTitle?: string;
  /** Titulo del caso: dice QUE se construyo, no el nombre del cliente. */
  title: string;
  /** Bajada de una linea para la tarjeta del indice. */
  summary: string;
  /** Prosa introductoria (para los casos que aun no tienen formato estructurado). */
  intro?: string[];
  /** Rubro del cliente, en una frase corta (ver caseProfiles.ts). */
  sector?: string;
  /** Quien es el cliente: perfil publico, con sus fuentes en `sources`. */
  about?: string;
  /** Datos del cliente (fundacion, tamaño, alcance). No son resultados de WAIA. */
  facts?: CaseFact[];
  /** El problema del cliente antes. */
  challenge?: string;
  /** Que desarrollo WAIA. */
  delivered?: string[];
  /** Que cambio despues. */
  impact?: string[];
  stats?: CaseStat[];
  tech?: string[];
}

export interface CaseStudy extends CaseStudyContent {
  slug: string;
  rubro: CaseRubro;
  /** Nombre completo del cliente, como se muestra. Igual en los dos idiomas. */
  client: string;
  /** Nombre corto, para el modificador de tamaño optico del logo. */
  brand: string;
  year?: string;
  /** Ciudad y pais del cliente, solo si esta confirmado. */
  location?: string;
  /** Donde se pueden verificar `about` y `facts`. */
  sources?: CaseSource[];
  logo?: string;
  logoAlt?: string;
  /** El logo va sobre un chip con fondo oscuro propio: para clientes cuyo
   * unico asset es la variante clara (pensada para su propio fondo oscuro),
   * que sobre la tarjeta blanca del indice no se lee sin un fondo detras. */
  logoBadge?: boolean;
  images?: CaseImage[];
  testimonial?: CaseTestimonial;
  /** Un caso en draft no se publica ni se pre-renderiza. */
  draft?: boolean;
  /**
   * Override en ingles de los campos de contenido. Un campo ausente cae al
   * valor en español antes que mostrar un hueco -es preferible una pagina en
   * ingles con una oracion en español a una seccion vacia.
   */
  en?: Partial<CaseStudyContent>;
}

/** Devuelve el caso con el contenido en el idioma pedido ya resuelto. */
export function localizeCase(study: CaseStudy, locale: Locale): CaseStudy {
  if (locale !== "en" || !study.en) return study;
  return { ...study, ...study.en };
}

/** Metadatos de cada rubro: etiqueta visible y clave de ruta de su indice. */
export const RUBROS: Record<
  CaseRubro,
  {
    label: string;
    labelEn: string;
    routeKey: "ecommerceCases" | "softwareCases" | "aiCases";
  }
> = {
  ecommerce: { label: "E-commerce", labelEn: "E-commerce", routeKey: "ecommerceCases" },
  "software-factory": {
    label: "Software Factory",
    labelEn: "Software Factory",
    routeKey: "softwareCases",
  },
  ai: {
    label: "Inteligencia Artificial",
    labelEn: "Artificial Intelligence",
    routeKey: "aiCases",
  },
};

/** El rubro del modelo -> la clave de textos del diccionario (t.casesIndex). */
export const CASES_COPY_KEY: Record<CaseRubro, "ecommerce" | "software" | "ai"> = {
  ecommerce: "ecommerce",
  "software-factory": "software",
  ai: "ai",
};

/**
 * Orden pedido por el cliente para el indice y el carrusel de la home:
 * Marriott, Grupo San Cristóbal, Crucijuegos, Sonder, ENAPRO, RiskRator,
 * Previnca, BoomID, Verona. Emerger no estaba en esa lista -se mantiene al
 * final en vez de sacarlo, ya que no pidio removerlo, solo no lo menciono al
 * ordenar el resto-.
 */
const baseCaseStudies: CaseStudy[] = [
  {
    slug: "elgrafico",
    rubro: "ai",
    client: "El Gráfico",
    brand: "elgrafico",
    year: "2026",
    logo: logoElGrafico,
    logoAlt: "El Gráfico",
    seoTitle: "El Gráfico: audio narrado con IA y voz clonada",
    title: "Audio narrado con IA, con la voz de sus propios periodistas",
    summary:
      "Generamos y publicamos automáticamente el audio de cada nota de El Gráfico, con clonación de voz de sus periodistas para más autenticidad.",
    challenge:
      "El Gráfico publica contenido deportivo a un ritmo muy alto, con buena parte de sus lectores navegando desde el celular en momentos en que leer una nota completa no es una opción -viajando, en el trabajo, mirando otra pantalla-. Ofrecer el audio como alternativa sin sumarle trabajo extra al equipo editorial no era algo que pudieran resolver grabando y editando a mano cada nota.",
    delivered: [
      "Implementación del widget de Audio Player: genera y publica automáticamente el audio de cada nota, sin intervención manual del equipo editorial.",
      "Clonación de la voz de periodistas de El Gráfico, para que el audio se escuche con la voz real de quien escribió la nota.",
      "Personalización visual del reproductor con la identidad de marca de El Gráfico.",
      "Integración liviana: un script embebido en el sitio, con carga asíncrona desde CDN para no afectar la velocidad de la página.",
    ],
    impact: [
      "El audio quedó integrado al flujo editorial sin sumarle trabajo al equipo: se genera y se publica solo, nota por nota.",
      "Los lectores pueden elegir escuchar la nota narrada con la voz real de quien la escribió, una experiencia que ningún otro medio deportivo en español ofrece hoy.",
      "La integración no afecta la velocidad de carga del sitio: el widget se sirve de forma asíncrona por CDN.",
    ],
    tech: [
      "Widget embebido (script)",
      "Clonación de voz con IA",
      "Carga asíncrona por CDN",
      "Personalización de marca",
    ],
    images: [
      {
        src: audioplayerElGraficoWidget,
        alt: "Reproductor de Audio Player integrado en una nota de El Gráfico",
      },
      {
        src: audioplayerElGraficoVoiceClone,
        alt: "Nota de El Gráfico narrada con la voz clonada de uno de sus periodistas",
      },
    ],
    en: {
      seoTitle: "El Gráfico: AI-narrated audio with cloned voices",
      title: "AI-narrated audio, with the voice of its own journalists",
      summary:
        "We automatically generate and publish audio for every El Gráfico article, with voice cloning for its journalists for extra authenticity.",
      challenge:
        "El Gráfico publishes sports content at a very high pace, with much of its readership browsing from their phone at moments when reading a full article isn't an option -commuting, at work, looking at another screen. Offering audio as an alternative without adding extra work for the editorial team wasn't something they could solve by manually recording and editing every article.",
      delivered: [
        "Implemented the Audio Player widget: automatically generates and publishes the audio for every article, with no manual work from the editorial team.",
        "Cloned the voices of El Gráfico journalists, so the audio plays in the real voice of whoever wrote the article.",
        "Customised the player's look with El Gráfico's brand identity.",
        "Lightweight integration: a script embedded on the site, loading asynchronously from a CDN so it doesn't affect page speed.",
      ],
      impact: [
        "Audio became part of the editorial workflow with no added work for the team: it's generated and published on its own, article by article.",
        "Readers can choose to listen to the article narrated in the real voice of whoever wrote it, an experience no other Spanish-language sports outlet offers today.",
        "The integration doesn't affect site load speed: the widget is served asynchronously via CDN.",
      ],
      tech: [
        "Embedded widget (script)",
        "AI voice cloning",
        "Asynchronous CDN loading",
        "Brand customisation",
      ],
    },
  },
  {
    slug: "sancristobal",
    rubro: "software-factory",
    client: "Grupo San Cristóbal",
    brand: "sancristobal",
    year: "2025",
    logo: logoSanCristobalColor,
    logoAlt: "Grupo San Cristóbal",
    seoTitle: "Grupo San Cristóbal: staffing de desarrollo senior",
    title: "Staffing de desarrollo de alto nivel técnico, sostenido durante un año",
    summary:
      "Un equipo dedicado que superó pruebas técnicas exigentes y sostuvo ese nivel trabajando para Grupo San Cristóbal, incluida su operación en Uruguay.",
    challenge:
      "Grupo San Cristóbal necesitaba sumar un equipo de desarrollo que sostuviera el mismo nivel técnico que exige su propio equipo interno, no solo para la operación central sino también para su división en Uruguay. Antes de confiarles el trabajo, sometió al equipo a varias pruebas técnicas exigentes para comprobarlo.",
    delivered: [
      "Equipo de staffing dedicado que superó las pruebas técnicas exigidas por Grupo San Cristóbal antes de sumarse al proyecto.",
      "Trabajo sostenido durante aproximadamente un año, con comunicación fluida y feedback constante.",
      "Sitios web para la operación de Grupo San Cristóbal en Uruguay, diseñados específicamente para convertir leads en ventas.",
    ],
    impact: [
      "El equipo sostuvo el nivel técnico exigido durante todo el año de trabajo, sin bajar la vara.",
      "La operación de Uruguay sumó sitios propios orientados a conversión, no solo presencia institucional.",
    ],
    en: {
      seoTitle: "Grupo San Cristóbal: senior development staffing",
      title: "Senior development staffing, sustained for a year",
      summary:
        "A dedicated team that passed demanding technical tests and kept up that bar working for Grupo San Cristóbal, including its Uruguay operation.",
      challenge:
        "Grupo San Cristóbal needed a development team that could sustain the same technical bar as its own internal team, not just for the core operation but for its Uruguay division too. Before trusting the team with the work, they put it through several demanding technical tests to check.",
      delivered: [
        "A dedicated staffing team that passed the technical tests Grupo San Cristóbal required before joining the project.",
        "Consistent work for about a year, with fluid communication and steady feedback.",
        "Websites for Grupo San Cristóbal's Uruguay operation, designed specifically to convert leads into sales.",
      ],
      impact: [
        "The team sustained the required technical level for the entire year of work, without ever dropping the bar.",
        "The Uruguay operation gained conversion-focused sites of its own, not just institutional presence.",
      ],
    },
  },
  {
    slug: "crucijuegos",
    rubro: "software-factory",
    client: "Crucijuegos",
    brand: "crucijuegos",
    seoTitle: "Crucijuegos: configurador inteligente de cotizaciones",
    title: "Configurador inteligente para automatizar cotizaciones de parques infantiles a medida",
    summary:
      "Un configurador web que automatiza un proceso de ingeniería complejo para que el equipo comercial arme propuestas personalizadas en minutos, integrado con AutoCAD.",
    logo: logoCrucijuegos,
    logoAlt: "Crucijuegos",
    challenge:
      "Crucijuegos diseña y fabrica parques infantiles personalizados para municipios, escuelas, clubes y espacios públicos, y cada proyecto es único. Antes de enviar una propuesta comercial hacía falta analizar el espacio disponible, definir los juegos compatibles, verificar restricciones técnicas, adaptar diseños en AutoCAD, calcular materiales y recién ahí armar la propuesta económica. Ese flujo dependía todo el tiempo del equipo técnico, lo que limitaba la capacidad del área comercial para responder rápido a nuevos clientes.",
    delivered: [
      "Configurador web para armar parques infantiles personalizados seleccionando módulos y componentes compatibles.",
      "Motor de reglas de negocio que valida automáticamente cada combinación según las restricciones técnicas definidas por la empresa, evitando configuraciones inválidas antes de llegar a ingeniería.",
      "Integración con la información de los diseños técnicos en AutoCAD, sin modificar el flujo de trabajo del equipo de ingeniería.",
      "Centralización de la información de cada configuración para avanzar rápido con la propuesta comercial.",
      "Arquitectura pensada para sumar nuevos productos, reglas comerciales e integraciones sin reconstruir la plataforma.",
    ],
    impact: [
      "Mayor autonomía del equipo comercial para configurar propuestas sin depender de ingeniería en cada paso.",
      "Reducción significativa del tiempo necesario para preparar una cotización.",
      "Menos errores por configuraciones manuales, validadas ahora en tiempo real.",
      "Estandarización del proceso comercial en toda la empresa.",
      "Más propuestas entregadas a tiempo se tradujo en más cotizaciones cerradas por el equipo comercial.",
      "Base tecnológica lista para automatizar más etapas del proceso a futuro.",
    ],
    stats: [
      { value: "+50%", label: "más rápido para entregar una propuesta comercial" },
    ],
    tech: [
      "Integración con AutoCAD",
      "Plataforma web a medida",
      "APIs de integración entre sistemas",
      "Motor de reglas de negocio",
      "Arquitectura escalable",
    ],
    en: {
      seoTitle: "Crucijuegos: smart configurator for custom quotes",
      title: "Smart configurator that automates custom playground quotes",
      summary:
        "A web configurator that automates a complex engineering process so the sales team can put together custom proposals in minutes, integrated with AutoCAD.",
      challenge:
        "Crucijuegos designs and manufactures custom playgrounds for municipalities, schools, clubs and public spaces, and every project is unique. Before sending a commercial proposal, the team had to analyse the available space, define compatible play structures, check technical constraints, adapt AutoCAD designs, calculate materials, and only then put together the pricing proposal. That workflow constantly depended on the technical team, which limited how fast the sales team could respond to new clients.",
      delivered: [
        "A web configurator to build custom playgrounds by selecting compatible modules and components.",
        "A business rules engine that automatically validates every combination against the company's technical constraints, catching invalid configurations before they reach engineering.",
        "Integration with the technical design data from AutoCAD, without changing the engineering team's workflow.",
        "Centralised configuration data so proposals move forward quickly.",
        "An architecture built to add new products, business rules and integrations without rebuilding the platform.",
      ],
      impact: [
        "More autonomy for the sales team to configure proposals without depending on engineering at every step.",
        "A significant cut in the time needed to prepare a quote.",
        "Fewer errors from manual configuration, now validated in real time.",
        "A standardised sales process across the company.",
        "More proposals delivered on time meant more quotes closed by the sales team.",
        "A technology base ready to automate further stages of the process down the line.",
      ],
      stats: [
        { value: "+50%", label: "faster to deliver a commercial proposal" },
      ],
      tech: [
        "AutoCAD integration",
        "Custom web platform",
        "System integration APIs",
        "Business rules engine",
        "Scalable architecture",
      ],
    },
  },
  {
    slug: "sonder",
    rubro: "ecommerce",
    client: "Sonder",
    brand: "sonder",
    seoTitle: "Sonder: e-commerce mayorista y minorista escalable",
    title: "Canal de ventas online mayorista y minorista, escalable",
    summary:
      "Optimizamos y escalamos su canal de ventas online con integraciones en tiempo real y un probador virtual con IA.",
    logo: sonderBlack,
    logoAlt: "Sonder",
    challenge:
      "Sonder necesitaba optimizar y escalar su canal de ventas online, tanto mayorista como minorista, en un entorno cada vez más competitivo. El proceso manual de conciliar stock, pagos y envíos entre canales se había vuelto un cuello de botella a medida que crecía el volumen de pedidos.",
    delivered: [
      "Integraciones en tiempo real de stock, medios de pago y envíos entre canales.",
      "Solución multitienda para una gestión más ágil y centralizada.",
      "Probador virtual basado en inteligencia artificial.",
    ],
    impact: [
      "Gestión más ágil y centralizada del canal mayorista y minorista, sin el cuello de botella manual de conciliar stock, pagos y envíos.",
    ],
    stats: [
      { value: "+50%", label: "de optimización de ganancias en ventas" },
      { value: "+30%", label: "de ahorro en pasarelas de pago" },
    ],
    images: [
      { src: sonderHeroTablet, alt: "Plataforma de e-commerce de Sonder en tablet" },
      { src: sonderVirtualTrial, alt: "Probador virtual con IA de Sonder" },
      { src: sonderProductMobile, alt: "Ficha de producto de Sonder en móvil" },
    ],
    en: {
      seoTitle: "Sonder: scalable wholesale and retail e-commerce",
      title: "A scalable wholesale and retail online sales channel",
      summary:
        "We optimised and scaled their online sales channel with real-time integrations and an AI-powered virtual try-on.",
      challenge:
        "Sonder needed to optimise and scale its online sales channel — both wholesale and retail — in an increasingly competitive market. Manually reconciling stock, payments and shipping across channels had become a bottleneck as order volume grew.",
      delivered: [
        "Real-time integrations for stock, payments and shipping across channels.",
        "A multi-store solution for faster, more centralised management.",
        "An AI-powered virtual try-on.",
      ],
      impact: [
        "Faster, more centralised management of the wholesale and retail channel, without the manual bottleneck of reconciling stock, payments and shipping.",
      ],
      stats: [
        { value: "+50%", label: "increase in sales profitability" },
        { value: "+30%", label: "savings on payment gateway fees" },
      ],
    },
  },
  {
    slug: "enapro",
    rubro: "ai",
    client: "Ente Administrador del Puerto de Rosario (ENAPRO)",
    brand: "enapro",
    logo: logoEnapro,
    logoAlt: "ENAPRO",
    seoTitle: "ENAPRO: experiencia con IA para el Puerto de Rosario",
    title: "Experiencia interactiva con Inteligencia Artificial para el Puerto de Rosario",
    summary:
      "Una experiencia interactiva con IA que acerca el puerto a empresas, visitantes y ciudadanos.",
    challenge:
      "ENAPRO buscaba generar una experiencia digital innovadora que permitiera acercar el puerto a empresas, visitantes y ciudadanos de una manera más interactiva y moderna, incrementando el tiempo de permanencia y el interés por sus contenidos. La comunicación institucional tradicional -folletos, cartelería, un sitio estático- ya no alcanzaba para transmitir la escala y la actividad real del puerto.",
    delivered: [
      "Desarrollo de una experiencia interactiva impulsada por Inteligencia Artificial.",
      "Asistente virtual capaz de responder consultas sobre el puerto y su funcionamiento.",
      "Navegación conversacional para facilitar el acceso a la información.",
      "Experiencia optimizada para dispositivos móviles y escritorio.",
      "Diseño orientado al engagement y la divulgación institucional.",
    ],
    impact: [
      "Mayor interacción de los usuarios con el contenido del puerto.",
      "Incremento del tiempo de permanencia dentro de la plataforma.",
      "Una experiencia institucional moderna que posiciona a ENAPRO como un organismo innovador en transformación digital.",
    ],
    en: {
      seoTitle: "ENAPRO: AI interactive experience for Port of Rosario",
      title: "An AI-powered interactive experience for the Port of Rosario",
      summary:
        "An AI-driven interactive experience that brings the port closer to businesses, visitors and citizens.",
      challenge:
        "ENAPRO wanted an innovative digital experience that would bring the port closer to businesses, visitors and citizens in a more interactive, modern way — increasing time spent on the platform and interest in its content. Traditional institutional communication — brochures, signage, a static website — could no longer convey the port's real scale and activity.",
      delivered: [
        "Built an interactive experience powered by artificial intelligence.",
        "A virtual assistant able to answer questions about the port and how it operates.",
        "Conversational navigation to make information easier to find.",
        "An experience optimised for both mobile and desktop.",
        "Design focused on engagement and institutional outreach.",
      ],
      impact: [
        "Higher user engagement with the port's content.",
        "Longer time spent on the platform.",
        "A modern institutional experience that positions ENAPRO as an innovator in digital transformation.",
      ],
    },
  },
  {
    slug: "riskrator",
    rubro: "software-factory",
    client: "RiskRator",
    brand: "riskrator",
    year: "2026",
    // Logo oficial en su variante oscura (la de riskrator.com, con las barras
    // de color de la marca), recortado al contenido: el original trae ~47% de
    // aire transparente arriba y abajo, y a 18-20px de alto el texto quedaba en
    // la mitad de ese tamaño. Se lee directo sobre la tarjeta blanca, asi que
    // ya no necesita el chip oscuro (logoBadge) que usaba con la variante clara.
    logo: logoRiskrator,
    logoAlt: "RiskRator",
    seoTitle: "RiskRator: automatización del assessment de riesgo",
    title: "Automatización del assessment de riesgo para acelerar el alta de clientes bancarios",
    summary:
      "Automatizamos el proceso de evaluación de clientes de RiskRator, reduciendo el alta de nuevos clientes en bancos de una semana a minutos.",
    challenge:
      "RiskRator es una plataforma de evaluación y gestión de riesgo financiero utilizada por bancos y entidades financieras, alineada a los estándares FFIEC, Basilea, FATF-GAFI, ISO y COSO. Uno de los procesos más sensibles para sus clientes bancarios es el assessment de riesgo de cada cliente nuevo: un análisis que, resuelto a mano, podía demorar hasta una semana completa antes de poder dar de alta a un cliente.",
    delivered: [
      "Automatización del proceso de assessment de usuarios, digitalizando un flujo que antes se resolvía a mano entre distintas áreas.",
      "Configuración y puesta a punto de la infraestructura necesaria para sostener el nuevo flujo automatizado.",
    ],
    impact: [
      "El tiempo de alta de un cliente nuevo en el banco bajó de una semana a 2 horas en la primera etapa del proyecto.",
      "Con las siguientes iteraciones, el tiempo promedio de alta se estabilizó en 30 minutos.",
      "Un proceso que dependía de la disponibilidad de varias personas pasó a resolverse solo, sin perder el control ni la trazabilidad que exige el rubro.",
    ],
    stats: [
      { value: "2 hs", label: "para dar de alta un cliente nuevo (antes, 1 semana)" },
      { value: "30 min", label: "de tiempo promedio de alta hoy" },
    ],
    en: {
      seoTitle: "RiskRator: risk assessment automation for banks",
      title: "Risk assessment automation to speed up new bank customer onboarding",
      summary:
        "We automated RiskRator's customer assessment process, cutting new bank customer onboarding from a week to minutes.",
      challenge:
        "RiskRator is a financial risk assessment and management platform used by banks and financial institutions, aligned with FFIEC, Basel, FATF-GAFI, ISO and COSO standards. One of the most sensitive processes for its banking clients is the risk assessment of every new customer — an analysis that, done manually, could take up to a full week before a new customer could be onboarded.",
      delivered: [
        "Automated the user assessment process, digitising a workflow that used to be resolved manually across different teams.",
        "Configured and tuned the infrastructure needed to support the new automated workflow.",
      ],
      impact: [
        "The time to onboard a new bank customer dropped from a week to 2 hours in the project's first stage.",
        "With later iterations, the average onboarding time settled at 30 minutes.",
        "A process that depended on several people's availability now resolves on its own, without losing the control or traceability the industry requires.",
      ],
      stats: [
        { value: "2h", label: "to onboard a new customer (down from 1 week)" },
        { value: "30 min", label: "average onboarding time today" },
      ],
    },
  },
  {
    slug: "previnca",
    rubro: "ai",
    // Nombre actualizado con el dato mas reciente que paso la empresa. El caso
    // original decia solo "Grupo Previnca": se conserva el mismo slug porque es
    // el mismo cliente y el mismo proyecto (agente de IA para turnos), ahora
    // con el contenido completo en vez del resumen generico inicial.
    client: "Grupo Previnca San Lorenzo",
    brand: "previnca",
    logo: logoPrevinca,
    logoAlt: "Grupo Previnca San Lorenzo",
    seoTitle: "Previnca: agente de IA por WhatsApp para turnos",
    title:
      "Agente de Inteligencia Artificial para atención, información y gestión de turnos por WhatsApp",
    summary:
      "María Pía: un asistente de IA por WhatsApp que centraliza turnos, información y atención las 24 horas.",
    year: "2026",
    challenge:
      "Grupo Previnca recibía un gran volumen de consultas repetitivas por WhatsApp y canales de atención tradicionales. Los usuarios necesitaban información sobre prestaciones, turnos y servicios, lo que generaba demoras, mayor carga para el personal y tiempos de espera innecesarios. Buena parte de esas consultas se repetía casi textual de un afiliado a otro, sin que existiera un canal capaz de resolverlas sin intervención humana.",
    delivered: [
      "Diseño e implementación del asistente virtual María Pía, disponible las 24 horas a través de WhatsApp.",
      "Integración con la base de conocimiento de la empresa para responder utilizando información oficial y siempre actualizada.",
      "Arquitectura basada en múltiples modelos de Inteligencia Artificial especializados según el tipo de consulta.",
      "Gestión y coordinación de turnos para distintas prestaciones.",
      "Derivación automática a los sectores correspondientes cuando la intervención humana resulta necesaria.",
      "Automatización de flujos de atención para disminuir consultas repetitivas y optimizar el trabajo del equipo administrativo.",
    ],
    impact: [
      "Centralización de la atención digital en un único canal.",
      "Reducción de la carga operativa del personal administrativo.",
      "Agilización de la gestión de consultas y turnos.",
      "Mejora en la experiencia de los afiliados gracias a respuestas inmediatas y disponibles las 24 horas.",
      "Contribución a la fidelización y retención de usuarios mediante una atención más rápida y consistente.",
    ],
    stats: [
      { value: "-40%", label: "en el volumen de consultas al call center" },
    ],
    tech: [
      "n8n",
      "Modelos de Inteligencia Artificial (LLMs)",
      "Integración con base de conocimiento corporativa",
      "Automatización de flujos",
      "WhatsApp Business",
    ],
    // Capturas: la empresa confirmo que se pueden publicar, pero los
    // archivos todavia no llegaron al repo. Se agregan en cuanto esten.
    en: {
      seoTitle: "Previnca: AI agent for WhatsApp appointments",
      title:
        "An AI agent for support, information and appointment management over WhatsApp",
      summary:
        "María Pía: a WhatsApp AI assistant that centralises appointments, information and support, 24 hours a day.",
      challenge:
        "Grupo Previnca was getting a high volume of repetitive queries through WhatsApp and traditional support channels. Users needed information about benefits, appointments and services, which caused delays, added workload for staff, and created unnecessary wait times. Many of those queries repeated almost word for word from one member to the next, with no channel able to resolve them without human intervention.",
      delivered: [
        "Designed and deployed the María Pía virtual assistant, available 24 hours a day over WhatsApp.",
        "Integrated it with the company's knowledge base, so answers always use official, up-to-date information.",
        "Built on an architecture with multiple AI models, each specialised for a type of query.",
        "Manages and coordinates appointments across different services.",
        "Automatically routes to the right department whenever a human needs to step in.",
        "Automated support workflows to cut down on repetitive queries and free up the administrative team.",
      ],
      impact: [
        "Digital support centralised into a single channel.",
        "Lower operational workload for administrative staff.",
        "Faster handling of queries and appointments.",
        "A better experience for members, with immediate answers available around the clock.",
        "Helped build loyalty and retention through faster, more consistent support.",
      ],
      stats: [
        { value: "-40%", label: "in call center query volume" },
      ],
      tech: [
        "n8n",
        "AI models (LLMs)",
        "Integration with the corporate knowledge base",
        "Workflow automation",
        "WhatsApp Business",
      ],
    },
  },
  {
    slug: "boomid",
    rubro: "software-factory",
    client: "BoomID",
    brand: "boomid",
    year: "2026",
    // SVG oficial de boomid.io (vectorial, nitido a cualquier tamaño y 5KB
    // contra los 28KB del raster anterior). Su sitio solo lo publica en blanco
    // para el navbar oscuro, asi que la tinta blanca se pasa al azul marino de
    // su propia identidad (#0d2e4d, el de su hoja de estilos) y el azul del
    // isotipo queda intacto: se lee directo sobre la tarjeta blanca, sin chip.
    logo: logoBoomid,
    logoAlt: "BoomID",
    seoTitle: "BoomID: infraestructura AWS de alta disponibilidad",
    title: "Infraestructura cloud de alta disponibilidad para una plataforma de identidad biométrica",
    summary:
      "Diseñamos y operamos toda la infraestructura AWS de BoomID: alta disponibilidad, auto-escalado, CI/CD y seguridad, con 99,9% de disponibilidad validada en producción.",
    challenge:
      "BoomID es una plataforma de autorización de identidad que reemplaza contraseñas y credenciales físicas por biometría de palma y motores de riesgo en tiempo real, usada por gobiernos, empresas e instituciones que necesitan confirmar con certeza quién está del otro lado de cada transacción o acceso. Una plataforma que valida identidad en tiempo real no puede darse el lujo de caerse: necesitaba infraestructura productiva, redundante y con un pipeline de despliegue confiable para escalar sin fricción.",
    delivered: [
      "Diseño e implementación de toda la infraestructura en AWS (ECS Fargate) para los servicios backend de la plataforma, en ambientes de test y producción.",
      "Arquitectura de alta disponibilidad multi-zona, con auto-escalado automático según demanda.",
      "Pipeline de CI/CD end-to-end, con promoción controlada de cambios entre ambientes.",
      "Monitoreo activo con alertas en tiempo real ante cualquier anomalía de infraestructura.",
      "Revisión y hardening continuo de la seguridad de la infraestructura.",
      "Documentación completa de la arquitectura y del pipeline para el equipo del cliente.",
    ],
    impact: [
      "99,9% de disponibilidad validada en el ambiente de producción.",
      "Escalado automático de 2 a 10 instancias por servicio según demanda, sin intervención manual.",
      "Redundancia real entre zonas de disponibilidad: la caída de una no afecta el servicio.",
      "Visibilidad completa del estado de la infraestructura, con alertas antes de que un problema llegue a impactar a un usuario.",
    ],
    tech: ["AWS ECS Fargate", "Terraform", "CI/CD", "CloudWatch", "Auto Scaling"],
    en: {
      seoTitle: "BoomID: high-availability AWS infrastructure",
      title: "High-availability cloud infrastructure for a biometric identity platform",
      summary:
        "We designed and run all of BoomID's AWS infrastructure: high availability, auto-scaling, CI/CD and security, with a validated 99.9% production SLA.",
      challenge:
        "BoomID is an identity authorization platform that replaces passwords and physical credentials with palm biometrics and real-time risk engines, used by governments, enterprises and institutions that need certainty about who's on the other side of every transaction or access request. A platform that verifies identity in real time can't afford downtime: it needed production-grade, redundant infrastructure and a reliable deployment pipeline to scale without friction.",
      delivered: [
        "Designed and built all the AWS infrastructure (ECS Fargate) for the platform's backend services, across test and production environments.",
        "High-availability, multi-zone architecture with automatic scaling based on demand.",
        "An end-to-end CI/CD pipeline, with controlled promotion of changes between environments.",
        "Active monitoring with real-time alerts for any infrastructure anomaly.",
        "Ongoing security review and hardening of the infrastructure.",
        "Complete architecture and pipeline documentation for the client's team.",
      ],
      impact: [
        "99.9% availability validated in the production environment.",
        "Automatic scaling from 2 to 10 instances per service based on demand, with no manual intervention.",
        "Real redundancy across availability zones: one going down doesn't affect the service.",
        "Full visibility into infrastructure health, with alerts before an issue reaches a user.",
      ],
      tech: ["AWS ECS Fargate", "Terraform", "CI/CD", "CloudWatch", "Auto Scaling"],
    },
  },
  {
    slug: "verona",
    rubro: "ecommerce",
    client: "Neumáticos Verona",
    brand: "verona",
    seoTitle: "Neumáticos Verona: digitalización de la venta online",
    title: "Digitalización de un rubro donde la venta online no era prioridad",
    summary:
      "Una solución 360 que combinó e-commerce y gestión de turnos en sucursal mediante tótems interactivos.",
    logo: verona,
    logoAlt: "Neumáticos Verona",
    challenge:
      "En un rubro donde la digitalización no era prioritaria, los clientes de Neumáticos Verona seguían resolviendo casi todo en el mostrador de la sucursal, incluso consultas que podrían resolverse online en minutos: la compra digital no era una alternativa real.",
    delivered: [
      "Nuevos canales digitales para incorporar la compra online como alternativa real, no solo un complemento del mostrador.",
      "Solución 360 que combina e-commerce con gestión de turnos en sucursal.",
      "Tótems interactivos para la gestión de turnos.",
    ],
    impact: [
      "La compra online pasó a ser una alternativa real, no solo el mostrador de la sucursal.",
      "La gestión de turnos en sucursal quedó integrada al mismo ecosistema digital que el e-commerce.",
      "Un bot de recupero por WhatsApp recontacta automáticamente a quien deja un carrito abandonado.",
    ],
    stats: [
      { value: "25%", label: "de los carritos abandonados se recuperan con el bot de WhatsApp" },
    ],
    images: [
      { src: veronaHeroTablets, alt: "Plataforma de Neumáticos Verona en tablets" },
      { src: veronaDesktopScreens, alt: "Plataforma de Neumáticos Verona en escritorio" },
    ],
    en: {
      seoTitle: "Neumáticos Verona: digitalising online sales",
      title: "Digitalising an industry where online sales weren't a priority",
      summary:
        "A 360 solution combining e-commerce with in-store queue management through interactive kiosks.",
      challenge:
        "In an industry where digitalisation wasn't a priority, Neumáticos Verona's customers kept resolving almost everything at the branch counter, even queries that could be solved online in minutes: buying online wasn't a real alternative.",
      delivered: [
        "New digital channels that made buying online a real alternative, not just a complement to the counter.",
        "A 360 solution combining e-commerce with in-store queue management.",
        "Interactive kiosks for queue management.",
      ],
      impact: [
        "Buying online became a real alternative, not just the branch counter.",
        "In-store queue management joined the same digital ecosystem as the e-commerce channel.",
        "A WhatsApp recovery bot automatically follows up on anyone who leaves an abandoned cart.",
      ],
      stats: [
        { value: "25%", label: "of abandoned carts are recovered by the WhatsApp bot" },
      ],
    },
  },
  {
    slug: "emerger",
    rubro: "software-factory",
    client: "Grupo Emerger",
    brand: "emerger",
    seoTitle: "Grupo Emerger: plataforma de videoconsultas",
    title: "Plataforma de videoconsultas para optimizar la atención de afiliados",
    summary:
      "Una app de videoconsultas que descomprimió la atención presencial y telefónica en los picos de demanda.",
    year: "2024",
    logo: logoEmerger,
    logoAlt: "Grupo Emerger",
    challenge:
      "Durante los meses de mayor demanda, especialmente en invierno, Grupo Emerger experimentaba un importante aumento en las consultas telefónicas, lo que obligaba a ampliar el personal de atención en sala. Esto incrementaba los costos operativos, generaba demoras para los afiliados y dificultaba mantener una experiencia de atención eficiente. El desafío no era solo de volumen: era de canal, porque la atención presencial y telefónica no podía escalar al mismo ritmo que la demanda de los afiliados.",
    delivered: [
      "Desarrollo de una aplicación móvil para realizar videoconsultas.",
      "Integración con el sistema de gestión existente del cliente.",
      "Plataforma para conectar afiliados con profesionales de manera remota.",
      "Optimización del flujo de atención para reducir la dependencia de la atención presencial y telefónica.",
      "Desarrollo sobre arquitectura escalable utilizando React Native, Node.js y MongoDB.",
    ],
    impact: [
      "Reducción de la cantidad de personas necesarias en la sala de atención durante los períodos de mayor demanda.",
      "Agilización del proceso de atención para los afiliados.",
      "Disminución de la carga de consultas telefónicas.",
      "Mejora en la experiencia de los usuarios gracias a un canal digital más rápido y accesible.",
      "La solución continúa en funcionamiento como parte del ecosistema digital del cliente.",
    ],
    tech: ["React Native", "Node.js", "MongoDB", "Integración con sistema de gestión existente"],
    images: [
      { src: emergerMobileApp, alt: "App de pacientes de Emerger en móvil" },
      { src: emergerManagementTablets, alt: "Portal de gestión de Emerger en tablet" },
    ],
    en: {
      seoTitle: "Grupo Emerger: video-consultation platform",
      title: "A video-consultation platform to streamline member care",
      summary:
        "A video-consultation app that eased in-person and phone-based care during peak demand.",
      challenge:
        "During peak-demand months, especially in winter, Grupo Emerger saw a sharp rise in phone consultations, which forced them to bring in more in-person staff. That drove up operating costs, created delays for members, and made it harder to keep the quality of care consistent. The challenge wasn't only about volume — it was about channel: in-person and phone care simply couldn't scale at the same pace as member demand.",
      delivered: [
        "Built a mobile app for video consultations.",
        "Integrated it with the client's existing management system.",
        "A platform connecting members with healthcare professionals remotely.",
        "Streamlined the care workflow to reduce reliance on in-person and phone care.",
        "Built on a scalable architecture using React Native, Node.js and MongoDB.",
      ],
      impact: [
        "Fewer staff needed in the care room during peak-demand periods.",
        "Faster care process for members.",
        "Lower volume of phone consultations.",
        "Better user experience thanks to a faster, more accessible digital channel.",
        "The solution is still running today as part of the client's digital ecosystem.",
      ],
      tech: ["React Native", "Node.js", "MongoDB", "Integration with the existing management system"],
    },
  },
  {
    slug: "marriott",
    rubro: "software-factory",
    client: "Marriott Bonvoy",
    brand: "marriott",
    seoTitle: "Marriott Bonvoy: campañas para captar socios",
    title: "Campañas digitales para captar socios en el Caribe y Latinoamérica",
    summary:
      "Landing pages integradas a sus sistemas para potenciar el programa de fidelización Marriott Bonvoy.",
    logo: marriott,
    logoAlt: "Marriott Bonvoy",
    challenge:
      "Marriott Hotels, que opera a nivel mundial, necesitaba impulsar campañas digitales en el Caribe y Latinoamérica para captar nuevos socios de su programa de fidelización Marriott Bonvoy. Las páginas genéricas de la marca no estaban pensadas para capturar a un usuario regional con una oferta puntual, y eso limitaba la conversión de las campañas locales.",
    delivered: [
      "Landing pages integradas a los sistemas de Marriott Bonvoy, pensadas para campañas regionales puntuales.",
    ],
    impact: [
      "Más usuarios captados para el programa de fidelización Marriott Bonvoy en el Caribe y Latinoamérica.",
      "Aumento exponencial de la llegada e impacto de las campañas en las zonas requeridas.",
    ],
    images: [
      { src: marriottLaptopMockup, alt: "Plataforma web de Marriott Bonvoy en laptop" },
      { src: marriottDashboardPanels, alt: "Paneles de dashboard de Marriott Bonvoy" },
    ],
    en: {
      seoTitle: "Marriott Bonvoy: digital campaigns to win members",
      title: "Digital campaigns to win new members across the Caribbean and Latin America",
      summary:
        "Landing pages integrated with their systems to boost the Marriott Bonvoy loyalty programme.",
      challenge:
        "Marriott Hotels, which operates worldwide, needed to run digital campaigns across the Caribbean and Latin America to win new members for its Marriott Bonvoy loyalty programme. The brand's generic pages weren't built to capture a regional user with a specific offer, which limited how well local campaigns converted.",
      delivered: [
        "Landing pages integrated with Marriott Bonvoy's systems, built for specific regional campaigns.",
      ],
      impact: [
        "More members signed up for the Marriott Bonvoy loyalty programme across the Caribbean and Latin America.",
        "An exponential increase in the reach and impact of campaigns in the target regions.",
      ],
    },
  },
];

/**
 * Suma el perfil publico del cliente (caseProfiles.ts) al caso. `stats`, `tech`
 * y `year` solo rellenan lo que el caso no trae: lo que WAIA ya publico manda.
 */
function applyProfile(study: CaseStudy): CaseStudy {
  const profile = caseProfiles[study.slug];
  if (!profile) return study;
  const en = study.en ?? {};
  return {
    ...study,
    location: profile.location,
    sources: profile.sources,
    sector: profile.sector,
    about: profile.about,
    facts: profile.facts,
    stats: study.stats ?? profile.stats,
    tech: study.tech ?? profile.tech,
    // La portada del perfil va primera: las capturas propias del caso quedan
    // como galeria detras.
    images: profile.cover ? [profile.cover, ...(study.images ?? [])] : study.images,
    en: {
      ...en,
      sector: profile.en.sector,
      about: profile.en.about,
      facts: profile.en.facts,
      stats: study.stats ? en.stats : profile.en.stats,
      tech: study.tech ? en.tech : profile.en.tech,
    },
  };
}

export const caseStudies: CaseStudy[] = baseCaseStudies.map(applyProfile);

/** Casos publicados (no draft). El resto no se lista ni se pre-renderiza. */
export const publishedCases = caseStudies.filter((c) => !c.draft);

export function casesByRubro(rubro: CaseRubro): CaseStudy[] {
  return publishedCases.filter((c) => c.rubro === rubro);
}

export function getCase(slug: string): CaseStudy | undefined {
  return publishedCases.find((c) => c.slug === slug);
}

/** Rutas de detalle a pre-renderizar: cada caso publicado, en los dos idiomas. */
export const caseRoutes: string[] = publishedCases.flatMap((c) =>
  LOCALES.map((locale) => `${CASE_PREFIX[locale]}/${c.slug}`),
);
